/**
 * Ovid's proof page on `@core/proof`. The sections derive from `../PROPOSITION.md`; the words are the Haiku seat's plain
 * rewrite, approved by the operator 2026-10-31, verbatim, except the numbers, read from the allowance
 * and the prices (both `proposition.ts`), never retyped. The page and the mail get the words through `ProofData`; the
 * home's FAQ (`wall/faq.ts`) reads the closing and own-muse lines from here.
 */
import {CURRENCY_SYMBOL} from '@core/billing/displayCurrency'
import type {ProofSpec} from '@core/proof/spec'
import {FREE_ALLOWANCE, FRONT_LINE, HERO_TITLE, MUSE_PRICE_UNITS, PLAN_ALLOWANCE, PLAN_PRICE_UNITS} from '@product/ovid/proposition'

const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen']
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']

/** A whole number below a thousand, in words. */
export function spell(n: number): string {
    if (n < 20) return ONES[n]
    if (n < 100) return TENS[Math.floor(n / 10)] + (n % 10 ? `-${ONES[n % 10]}` : '')
    return `${ONES[Math.floor(n / 100)]} hundred${n % 100 ? ` ${spell(n % 100)}` : ''}`
}

const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)
const dollar = CURRENCY_SYMBOL.usd

export const CLOSING_TITLE = `${sentence(spell(FREE_ALLOWANCE))} pictures a day, free`
export const CLOSING_BODY =
    `Start with ${FREE_ALLOWANCE} pictures a day, free. The plan gives ${PLAN_ALLOWANCE} a day and one woman made from ` +
    `your photos, for ${dollar}${PLAN_PRICE_UNITS} a month; extra ones cost ${dollar}${MUSE_PRICE_UNITS} each.`

export const OWN_MUSE_BODY =
    `Send 6 to 20 photos of a woman. Ovid makes her in a few hours and tells you when she is ready, then she can appear ` +
    `in any picture you make. The plan includes one; each extra one costs ${dollar}${MUSE_PRICE_UNITS}.`

export {HERO_TITLE}

export const ovidProofSpec: ProofSpec = {
    product: 'ovid',
    domain: 'ovid.gallery',
    searchQuery: '',
    catalogueSlugs: [],
    chips: [],
    demos: [
        {
            kind: 'themes',
            title: 'Find your fantasy',
            body: 'Thousands of pictures sorted by theme, such as baths and steam, costumes, the wedding night or after hours at the office. What you imagined is probably already there.',
        },
        {
            kind: 'neighbours',
            title: 'It never runs out',
            body: 'Every picture leads to more pictures like it. New ones are added all the time.',
        },
        {
            kind: 'muse',
            title: 'The same woman in every picture',
            body: 'Choose one woman from 32. Every picture you make shows her, with the same face.',
        },
        {
            kind: 'ownMuse',
            title: 'Your own woman, from photos',
            body: OWN_MUSE_BODY,
            steps: ['Photos sent', 'Being made', 'Ready in every picture'],
        },
        {
            kind: 'edit',
            title: 'Change any picture',
            body: "Say in your own words what to change: her pose, the place, or what she wears or doesn't.",
        },
    ],
    copy: {
        hero: {
            title: HERO_TITLE,
            line: FRONT_LINE,
        },
        mailHeadline: HERO_TITLE,
        demosHeading: null,
        chipsHeading: null,
        picksHeading: 'Picked for you',
        closing: {title: CLOSING_TITLE, body: CLOSING_BODY, link: 'Start browsing', to: '/'},
        tags: {pairFrom: '', pairWith: '', before: 'before', after: 'after', packs: 'sets', pictures: 'pictures'},
        alts: {
            sample: 'A picture from the wall',
            shown: '{name}, a made-up person',
            pairWith: 'A picture from the wall',
            editBefore: 'The picture before',
            editAfter: 'The picture after',
        },
    },
}
